import React, { createContext, useState, useContext, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';

import { DB } from '~/services/firebase';
import ICategory from '~/interfaces/ICategory';
import { useAuth } from './auth';

interface CategoriesCompanyContextData {
  categories: ICategory[];
  loadCategories(): Promise<void>;
  clearContext(): void;
  updateCategories(category: ICategory, isNew?: boolean): void;
  removeCategory(id: string): void;
  isInitialized: boolean;
}

const CategoriesCompanyContext = createContext<CategoriesCompanyContextData>(
  {} as CategoriesCompanyContextData,
);

export const CategoriesCompanyProvider: React.FC = ({ children }) => {
  const { user } = useAuth();
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  const clearContext = useCallback(() => {
    setCategories([]);
  }, []);

  const updateCategories = useCallback(
    (category: ICategory, isNew = false) => {
      let items = [...categories];

      if (isNew) {
        items.push(category);

        items = items.sort((a, b) =>
          a.order > b.order ? 1 : b.order > a.order ? -1 : 0,
        );

        setCategories(items);
      } else {
        const findIndex = items.findIndex(item => item.uid === category.uid);

        if (findIndex !== -1) {
          items[findIndex] = category;

          items = items.sort((a, b) =>
            a.order > b.order ? 1 : b.order > a.order ? -1 : 0,
          );

          setCategories(items);
        }
      }
    },
    [categories],
  );

  const removeCategory = useCallback(
    (id: string) => {
      const items = [...categories];

      const findIndex = items.findIndex(item => item.uid === id);

      if (findIndex !== -1) {
        items.splice(findIndex, 1);
        setCategories(items);
      }
    },
    [categories],
  );

  const loadCategories = useCallback(async () => {
    if (!isInitialized) {
      const categoriesRef = collection(DB, 'categories');
      const q = query(categoriesRef, where('user_id', '==', user.uid));
      const querySnapshot = await getDocs(q);

      const items: ICategory[] = [];

      querySnapshot.docs.forEach(item => {
        const parseItem = {
          ...item.data(),
          uid: item.id,
        } as ICategory;

        items.push(parseItem);
      });

      items.sort((a, b) =>
        a.order > b.order ? 1 : b.order > a.order ? -1 : 0,
      );

      setCategories(items);
      setIsInitialized(true);
    }
  }, [user, isInitialized]);

  return (
    <CategoriesCompanyContext.Provider
      value={{
        categories,
        loadCategories,
        clearContext,
        isInitialized,
        updateCategories,
        removeCategory,
      }}
    >
      {children}
    </CategoriesCompanyContext.Provider>
  );
};

export function useCategoriesCompany(): CategoriesCompanyContextData {
  const context = useContext(CategoriesCompanyContext);
  return context;
}
